import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'Supabase is not configured. Copy .env.example to .env.local and fill in ' +
      'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY from your project settings.',
  );
}

/**
 * The anon key is safe to ship — it grants nothing on its own. Every table has
 * row level security enabled and default-deny policies, so what this client can
 * read is decided by the signed-in user's role, in the database, not here.
 */
export const supabase: SupabaseClient<Database> = createClient<Database>(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // The field app is a webview with no address bar; PKCE is the right flow.
    flowType: 'pkce',
  },
  global: {
    headers: { 'x-noorone-client': 'web' },
  },
});

/* -----------------------------------------------------------------------------
 * Files are not here.
 *
 * Uploads, viewing and deletion moved to ImageKit — see src/core/files.ts. The
 * `BUCKETS` map and `signedUrl()` that used to live here are gone rather than
 * deprecated, so that nothing can keep half-using Supabase Storage and leave
 * files split across two systems with two different access models.
 *
 * The access rules did not move with them: every file is uploaded private, and
 * a viewing URL is only minted after RLS on `file_assets` has agreed the caller
 * may see that file. See supabase/migrations/0022_imagekit.sql.
 * -------------------------------------------------------------------------- */
